/**
 * Home ウィジェットの外枠。
 *
 * カード全体が1つのリンクになるものは WidgetCard、
 * 中に複数のタップ先を持つものは WidgetShell を使う（button の入れ子を避けるため）。
 * 高さはグリッドのセルで決まるので h-full にして中身を詰める。
 */

import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { clampRatio } from '@/components/home/home-widget-data'
import { cn } from '@/lib/utils'

const SHELL_CLASS =
  'flex h-full w-full flex-col gap-1.5 overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-3 text-left'

type WidgetHeaderProps = {
  icon: LucideIcon
  label: string
  accentClassName?: string
}

const WidgetHeader = ({ icon: Icon, label, accentClassName }: WidgetHeaderProps) => (
  <div className="flex items-center gap-1.5">
    <Icon size={13} className={cn('shrink-0 text-muted-foreground', accentClassName)} />
    <span className="truncate text-[11px] font-medium text-muted-foreground">{label}</span>
  </div>
)

type WidgetProps = WidgetHeaderProps & {
  className?: string
  children: ReactNode
}

export const WidgetCard = ({
  icon,
  label,
  accentClassName,
  className,
  children,
  onClick,
}: WidgetProps & { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={cn(SHELL_CLASS, 'active:opacity-70', className)}
  >
    <WidgetHeader icon={icon} label={label} accentClassName={accentClassName} />
    <div className="min-h-0 min-w-0 flex-1">{children}</div>
  </button>
)

export const WidgetShell = ({ icon, label, accentClassName, className, children }: WidgetProps) => (
  <div className={cn(SHELL_CLASS, className)}>
    <WidgetHeader icon={icon} label={label} accentClassName={accentClassName} />
    <div className="min-h-0 min-w-0 flex-1">{children}</div>
  </div>
)

/** 1x1 用。アイコン1つ分の幅しかないので数値ひとつに絞る */
export const WidgetTile = ({
  icon: Icon,
  label,
  value,
  accentClassName,
  onClick,
}: WidgetHeaderProps & { value: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`${label} ${value}`}
    className="flex h-full w-full flex-col items-center justify-between overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-2 active:opacity-70"
  >
    <Icon size={20} className={cn('shrink-0 text-muted-foreground', accentClassName)} />
    <span className="max-w-full truncate text-xl leading-none font-semibold tabular-nums">{value}</span>
    <span className="max-w-full truncate text-[10px] leading-none text-muted-foreground">{label}</span>
  </button>
)

/** 1x2 / 1x4 用。ヘッダーを小さく載せて、残りの幅をすべて本文に回す */
export const WidgetRow = ({
  icon,
  label,
  value,
  unit,
  sub,
  accentClassName,
  onClick,
  children,
}: WidgetHeaderProps & {
  value: string
  unit?: string
  sub?: string
  onClick: () => void
  children?: ReactNode
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`${label} ${value}`}
    className="flex h-full w-full flex-col gap-1 overflow-hidden rounded-2xl border border-border/60 bg-card/60 px-3 py-1.5 text-left active:opacity-70"
  >
    <WidgetHeader icon={icon} label={label} accentClassName={accentClassName} />
    <div className="flex min-h-0 w-full flex-1 items-center gap-2">
      {/* 桁数で本文の幅が変わらないよう固定幅にする */}
      <div className="w-16 shrink-0">
        <div className="flex items-baseline gap-0.5">
          <span className="truncate text-lg leading-none font-semibold tabular-nums">{value}</span>
          {unit && <span className="text-[10px] leading-none text-muted-foreground">{unit}</span>}
        </div>
        {sub && <div className="mt-1 truncate text-[9px] leading-none text-muted-foreground tabular-nums">{sub}</div>}
      </div>
      {children && <div className="min-w-0 flex-1">{children}</div>}
    </div>
  </button>
)

/** 達成度を出す細いバー。className は伸びる側、trackClassName は土台側 */
export const WidgetBar = ({
  ratio,
  className,
  trackClassName,
}: {
  ratio: number
  className: string
  trackClassName?: string
}) => (
  <div className={cn('h-1.5 overflow-hidden rounded-full bg-muted', trackClassName)}>
    <div className={cn('h-full rounded-full', className)} style={{ width: `${clampRatio(ratio) * 100}%` }} />
  </div>
)

export const WidgetSkeleton = ({ className }: { className?: string }) => (
  <div className={cn(SHELL_CLASS, 'animate-pulse', className)}>
    <div className="h-2.5 w-14 rounded-full bg-muted" />
    <div className="mt-1 h-5 w-20 rounded-md bg-muted" />
  </div>
)

/** データが無いときの中身。枠を消すとレイアウトが動くので文言だけ差し替える */
export const WidgetEmpty = ({ message }: { message: string }) => (
  <div className="flex h-full items-center text-[11px] text-muted-foreground">{message}</div>
)
